import React, { useMemo, useState } from "react";
import Table from "../../../../../../components/TableV2";
import TitleButtons from "../../../../../../components/TitleButtons";
import { useNavigate } from "react-router-dom";
import { generatePdfReport } from "../../../../../../utils/pdf/generatePdfReport";
import { useProdutosDisponiveis } from "./produtoData";
import TableButtons from "../../../../../../components/TableButtons";
import TableActions from "../../../../../../components/TableActions";

const DiretoriaEstoque: React.FC = () => {
  const navigate = useNavigate();
  const { produtos, loading } = useProdutosDisponiveis();

  const headers = useMemo(
    () => [
      "Código",
      "Nome do Produto",
      "Q.",
      "Custo Unitário",
      "Moeda", // <<< nova coluna
      "Margem (%)",
      "Preço de Venda",
      "Vendável",
      "Classificação",
      "Posição",
    ],
    []
  );

  const columnWidths = useMemo(
    () => [
      "100px", // Código
      "300px", // Nome
      "50px", // Quantidade
      "120px", // Custo Unitário
      "80px", // Moeda
      "110px", // Margem
      "130px", // Preço de Venda
      "20px", // Vendável
      "70px", // Classificação
      "80px", // Posição
    ],
    []
  );

  // --- helpers de formatação ---
  const formatNumberPtBR = (v: any) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "N/A";
    return n.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatMoneyByCurrency = (value: any, moeda?: string | null) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "N/A";
    const num = n.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const sym =
      String(moeda || "").toUpperCase() === "BRL"
        ? "R$"
        : String(moeda || "").toUpperCase() === "USD"
        ? "$"
        : String(moeda || "").toUpperCase() === "EUR"
        ? "€"
        : "";
    return sym ? `${sym} ${num}` : num;
  };

  const formatPercent = (v: any) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "0";
    return n.toLocaleString("pt-BR", { maximumFractionDigits: 2 });
  };

  const rows = useMemo(
    () =>
      produtos.map((p: any) => {
        // aceita tanto custo_unitario quanto custo_unitario_atual
        const custo = p.custo_unitario ?? p.custo_unitario_atual ?? null;
        const moeda = (p.moeda ?? p.currency ?? null) as string | null;

        const margem = Number(p.margem_percentual ?? 0);
        const quantidade = p.quantidade_atual ?? p.quantidade ?? 0;

        // preço de venda: se margem = 0 ou inválida → "pendente"
        let precoVendaStr = "pendente";
        if (
          Number.isFinite(Number(custo)) &&
          Number.isFinite(margem) &&
          margem !== 0
        ) {
          const precoVenda = Number(custo) * (1 + margem / 100);
          precoVendaStr = formatMoneyByCurrency(precoVenda, moeda);
        }

        const vendavelLabel =
          typeof p.vendavel === "string"
            ? p.vendavel
            : p.vendavel == null
            ? "N/A"
            : p.vendavel
            ? "Sim"
            : "Não";

        return [
          p.codigo,
          p.nome,
          String(quantidade),
          formatMoneyByCurrency(custo, moeda), // custo formatado por moeda
          (moeda ?? "—").toString().toUpperCase(), // coluna Moeda
          formatPercent(margem),
          precoVendaStr,
          vendavelLabel,
          p.classificacao,
          p.posicao,
        ];
      }),
    [produtos]
  );

  const [selectedColumns, setSelectedColumns] = useState<boolean[]>(
    headers.map(() => true)
  );

  const enabledColumnIndexes = useMemo(
    () =>
      selectedColumns
        .map((checked, i) => (checked ? i : -1))
        .filter((i) => i !== -1),
    [selectedColumns]
  );

  const firstCode = rows.length ? rows[0][0] : "—";
  const lastCode = rows.length ? rows[rows.length - 1][0] : "—";
  const nextCode = `PRD001${(produtos.length + 1).toString().padStart(2, "0")}`;

  // --------- Coloração condicional (compatível com Table novo) ----------
  const getCellStyle = (
    _viewIndex: number,
    _colIndex: number,
    _value: string | number | React.ReactNode,
    originalIndex: number,
    _rowData: (string | number | React.ReactNode)[],
    headerTitle: string
  ) => {
    const p = produtos[originalIndex];
    if (!p) return undefined;

    const GREEN_PASTEL = "#e8f7ec"; // verde claro
    const RED_PASTEL = "#fdeaea"; // vermelho claro

    if (headerTitle === "Vendável") {
      const isSim =
        (typeof p.vendavel === "string" &&
          p.vendavel.trim().toLowerCase() === "sim") ||
        (typeof p.vendavel !== "string" && !!p.vendavel);
      const isNao =
        (typeof p.vendavel === "string" &&
          (p.vendavel.trim().toLowerCase() === "não" ||
            p.vendavel.trim().toLowerCase() === "nao")) ||
        (typeof p.vendavel !== "string" && p.vendavel === false);

      if (isSim) return { background: GREEN_PASTEL };
      if (isNao) return { background: RED_PASTEL };
      return undefined;
    }

    if (headerTitle === "Margem (%)") {
      const mg = Number(p.margem_percentual ?? 0);
      if (!Number.isFinite(mg)) return undefined;
      return { background: mg < 10 ? RED_PASTEL : GREEN_PASTEL };
    }

    if (headerTitle === "Preço de Venda") {
      // destacar "pendente"
      const margem = Number(p.margem_percentual ?? 0);
      if (!Number.isFinite(margem) || margem === 0) {
        return { background: RED_PASTEL };
      }
    }

    return undefined;
  };
  // ----------------------------------------------------------------------

  return (
    <div>
      <TitleButtons fullBleedLine title="Diretoria - Estoque Administrativo" />
      <TableActions
        buttons={[
          {
            label: "Gerar Relatório",
            onClick: () =>
              generatePdfReport({
                reportTitle: "Relatório de Produtos - Diretoria",
                fileNamePrefix: "Diretoria-Produtos",
                subtitle: "Informações dos produtos registrados",
                headers,
                rows,
                custoColIndex: 3, // permanece "Custo Unitário"
                enabledColumns: enabledColumnIndexes,
                footerLines: [
                  rows.length
                    ? `Códigos Utilizados: ${firstCode} - ${lastCode}`
                    : "Sem produtos",
                  `Próximo Código Disponível: ${nextCode}`,
                ],
              }),
            variant: "secondary",
            dropdownOptions: headers.map((h, i) => ({
              label: h,
              checked: selectedColumns[i],
            })),
            onOptionChange: (index, checked) => {
              const updated = [...selectedColumns];
              updated[index] = checked;
              setSelectedColumns(updated);
            },
          },
          {
            label: "Realizar Movimentação",
            onClick: () => navigate("/diretoria/estoque/realizar-movimentacao"),
            variant: "primary",
          },
        ]}
      />

      <Table
        headers={headers}
        rows={rows}
        columnWidths={columnWidths}
        responsiveWidths={{
          1300: {
            1: "100px",
            2: "260px",
            3: "90px",
            4: "110px",
            5: "70px", // Moeda
            6: "90px",
            7: "120px",
            8: "90px",
            9: "110px",
            10: "70px",
          },
          1200: {
            1: "100px",
            2: "220px",
            3: "90px",
            4: "110px",
            5: "70px", // Moeda
            6: "90px",
            7: "120px",
            8: "90px",
            9: "110px",
            10: "70px",
          },
        }}
        // >>> ativa a coloração condicional
        enableCellColors={true}
        getCellStyle={getCellStyle}
        onDoubleClick={(_viewIndex, rowData) => {
          const codigoIdx = headers.indexOf("Código");
          const codigo = String(rowData[codigoIdx] ?? "");
          if (codigo) {
            navigate(`/diretoria/produtos/${encodeURIComponent(codigo)}`);
          }
        }}
      />

      {loading && (
        <div style={{ padding: 16, opacity: 0.7, fontSize: 14 }}>
          Carregando produtos…
        </div>
      )}
    </div>
  );
};

export default DiretoriaEstoque;
